import axios from 'axios';

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
const API_BASE_URL = configuredApiBaseUrl && configuredApiBaseUrl.length > 0
  ? configuredApiBaseUrl
  : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Reduced timeout for initial request
  withCredentials: true, // Include cookies in requests
});

// Create a separate instance for long-running requests
const longApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for long requests
  withCredentials: true, // Include cookies in requests
});

export interface ValidationFlag {
  type: string;
  description: string;
  field: string;
  systemValue?: string;
  factSheetValue?: string;
  expectedValue?: string;
  requirement?: string;
  riskLevel?: string;
  previousValue?: string;
  change?: string;
}

export interface PreviousYearData {
  volumetricWaterBenefit: string;
  costShare?: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  type?: 'new' | 'historic';
  category?: 'reforestation' | 'wetland' | 'efficiency' | 'infrastructure';
  status?: 'clean' | 'stage1' | 'stage2' | 'non-eligible';
  stage?: string;
  flags?: string; // JSON string
  basin?: string;
  volumetricWaterBenefit?: string;
  costShare?: string;
  quantificationMethod?: string;
  affectedFieldImpact?: string;
  previousYearData?: string; // JSON string
  hasComparison?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetails extends Project {
  factSheets: (FactSheet & {
    analyses: Analysis[];
  })[];
}

export interface FactSheet {
  id: string;
  projectId?: string;
  filename: string;
  originalName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  status: 'UPLOADED' | 'PROCESSING' | 'PROCESSED' | 'ERROR';
  createdAt: string;
  updatedAt: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  factSheet?: FactSheet;
  project?: Project;
  error?: string;
}

export interface VWBAInput {
  name: string;
  type: 'numeric' | 'text' | 'date' | 'boolean' | 'file';
  unit?: string;
  description: string;
  required: boolean;
  value?: any;
  available: boolean;
  source?: string;
}

export interface VWBAMethodology {
  name: string;
  description: string;
  confidence: number;
  reasoning: string;
  inputs: VWBAInput[];
  calculationSteps: string[];
  references: string[];
}

export interface VWBAAnalysis {
  projectName: string;
  projectDescription: string;
  recommendedMethodology: VWBAMethodology;
  alternativeMethodologies: VWBAMethodology[];
  extractedData: Record<string, any>;
  missingInputs: VWBAInput[];
  availableInputs: VWBAInput[];
  confidence: number;
  processingNotes: string[];
}

export interface Analysis {
  id: string;
  factSheetId: string;
  methodology: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ERROR';
  inputs?: string;
  results?: string;
  rawResponse?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AnalysisResponse {
  success: boolean;
  message?: string;
  analysis?: Analysis;
  vwbaAnalysis?: VWBAAnalysis;
  rawResponse?: string;
  error?: string;
}

export interface HealthResponse {
  status: string;
  database: {
    connected: boolean;
    migrationsApplied: number;
  };
  timestamp: string;
}

// Authentication
export const login = async (password: string): Promise<{ success: boolean }> => {
  const response = await api.post('/auth/login', { password });
  return response.data;
};

export const logout = async (): Promise<{ success: boolean }> => {
  const response = await api.delete('/auth/login');
  return response.data;
};

// Health check
export const checkHealth = async (): Promise<HealthResponse> => {
  const response = await api.get('/health');
  return response.data;
};


// Get projects with optional filters
export const getProjects = async (filters?: {
  status?: string;
  type?: string;
  category?: string;
}): Promise<Project[]> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.category) params.append('category', filters.category);

  const url = params.toString() ? `/projects?${params.toString()}` : '/projects';
  const response = await api.get(url);
  return response.data;
};

// Get project details
export const getProjectDetails = async (projectId: string): Promise<ProjectDetails> => {
  const response = await api.get(`/projects/${projectId}`);
  return response.data;
};

// Create project
export const createProject = async (projectData: {
  name: string;
  description?: string;
  type?: 'new' | 'historic';
  category?: 'reforestation' | 'wetland' | 'efficiency' | 'infrastructure';
  status?: 'clean' | 'stage1' | 'stage2' | 'non-eligible';
  stage?: string;
  flags?: ValidationFlag[];
  basin?: string;
  volumetricWaterBenefit?: string;
  costShare?: string;
  quantificationMethod?: string;
  affectedFieldImpact?: string;
  previousYearData?: PreviousYearData;
  hasComparison?: boolean;
}): Promise<Project> => {
  const response = await api.post('/projects', projectData);
  return response.data;
};

// Match project by name and location
export interface MatchProjectResponse {
  match: boolean;
  project: Project | null;
}

export const matchProject = async (name: string, basin: string): Promise<MatchProjectResponse> => {
  const response = await api.post('/projects/match', { name, basin });
  return response.data;
};

// Update project
export const updateProject = async (
  projectId: string,
  data: {
    name?: string;
    description?: string;
    type?: 'new' | 'historic';
    category?: 'reforestation' | 'wetland' | 'efficiency' | 'infrastructure';
    status?: 'clean' | 'stage1' | 'stage2' | 'non-eligible';
    stage?: string;
    flags?: ValidationFlag[];
    basin?: string;
    volumetricWaterBenefit?: string;
    costShare?: string;
    quantificationMethod?: string;
    affectedFieldImpact?: string;
    previousYearData?: PreviousYearData;
    hasComparison?: boolean;
  }
): Promise<Project> => {
  const response = await api.patch(`/projects/${projectId}`, data);
  return response.data;
};

// Upload file
export const uploadFile = async (file: File): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// Analyze fact sheet
export const analyzeFactSheet = async (factSheetId: string): Promise<AnalysisResponse> => {
  const response = await longApi.post('/analyze', { factSheetId });
  return response.data;
};

// Get analysis results
export const getAnalysis = async (factSheetId: string): Promise<AnalysisResponse> => {
  const response = await api.get(`/analyze?factSheetId=${factSheetId}`);
  return response.data;
};

// Delete project
export const deleteProject = async (projectId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/projects/${projectId}`);
  return response.data;
};

// Associate fact sheet with project
export const associateFactSheetWithProject = async (factSheetId: string, projectId: string): Promise<{ message: string }> => {
  const response = await api.post(`/projects/${projectId}/fact-sheets`, { factSheetId });
  return response.data;
};

// Delete fact sheet
export const deleteFactSheet = async (factSheetId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/fact-sheets/${factSheetId}`);
  return response.data;
};

// Get authenticated file URL
export const getAuthenticatedFileUrl = async (filename: string): Promise<string> => {
  const response = await api.get(`/files/authenticated?filename=${encodeURIComponent(filename)}`, {
    responseType: 'blob'
  });

  // Create a blob URL from the response
  const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
  return URL.createObjectURL(blob);
};

// Seed database response
export interface SeedResponse {
  success: boolean;
  dataset?: 'default' | 'factsheet-scenarios';
  message?: string;
  error?: string;
  stats?: {
    deleted: {
      projects: number;
      factSheets: number;
      analyses: number;
    };
    created: {
      projects: number;
      factSheets: number;
      analyses: number;
    };
  };
}

// Seed database (delete all data and re-seed)
export const seedDatabase = async (
  dataset: 'default' | 'factsheet-scenarios' = 'factsheet-scenarios'
): Promise<SeedResponse> => {
  const seedUrl = dataset === 'factsheet-scenarios' ? '/seed?dataset=factsheet-scenarios' : '/seed';
  const response = await longApi.post(seedUrl);
  return response.data;
};

export default api;
